import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Doughnut, Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
} from "chart.js";
import myAxios from "../../_services/axiositeachWrapper";
import Config from "../../_config/config";
import { applyChartJsTheme, getChartPalette, getToken, useThemeVersion } from "../../theme/chartTheme";

ChartJS.register(ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement);

// Token-driven axis/grid defaults; re-applied whenever the theme or mode changes
applyChartJsTheme(ChartJS);
window.addEventListener("themechange", () => applyChartJsTheme(ChartJS));

interface PathwayDist {
  pathwayId: string;
  pathwayName: string;
  count: number;
  percentage: number;
}

interface ScoreDist {
  range: string;
  count: number;
}

interface Submission {
  id: number;
  userId: number;
  userName: string;
  selectedPathway: string;
  pathwayName: string;
  score: number | null;
  justification: string | null;
  aiFeedback: string | null;
  aiStrengths: string[] | null;
  aiImprovements: string[] | null;
  submittedAt: string;
  module: number;
}

interface AnalyticsData {
  summary: {
    totalSubmissions: number;
    avgScore: number;
    avgTimeSpent: number;
    scoredCount: number;
  };
  pathwayDistribution: PathwayDist[];
  scoreDistribution: ScoreDist[];
  recentSubmissions: Submission[];
}

const PathwayAnalyticsPage: React.FC = () => {
  // Token-resolved chart colors — recomputed on light/dark toggle
  const themeVersion = useThemeVersion();
  const COLORS = React.useMemo(() => getChartPalette(), [themeVersion]);
  // doughnut slice separator should match the card surface in both modes
  const sliceBorder = React.useMemo(() => getToken("--surface-card") || "#fff", [themeVersion]);
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const resourceId = params.get("resourceId");

  const [data, setData] = useState<AnalyticsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [expandedRow, setExpandedRow] = useState<number | null>(null);

  useEffect(() => {
    if (!resourceId) {
      setError("No resourceId provided. Access this page from a study resource.");
      setLoading(false);
      return;
    }
    fetchAnalytics();
  }, [resourceId]);

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      const res: any = await myAxios.get(
        `${Config.nodeSiteUrl}v1/analytics/pathway-submissions?resourceId=${resourceId}`
      );
      if (res.data?.success) {
        setData(res.data.data);
      } else {
        setError(res.data?.error || "Failed to load analytics");
      }
    } catch (err: any) {
      setError(err.message || "Failed to load analytics");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div style={{ padding: 40, textAlign: "center", color: "var(--text-muted)" }}>
        Loading analytics...
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: 40, textAlign: "center", color: "var(--status-danger)" }}>
        {error}
      </div>
    );
  }

  if (!data || data.summary.totalSubmissions === 0) {
    return (
      <div style={{ padding: 40, textAlign: "center", color: "var(--text-muted)" }}>
        <h4 style={{ marginBottom: 8 }}>No submissions yet</h4>
        <p>Students need to complete the Interactive Pathway exercise before analytics appear here.</p>
      </div>
    );
  }

  const doughnutData = {
    labels: data.pathwayDistribution.map((p) => p.pathwayName),
    datasets: [
      {
        data: data.pathwayDistribution.map((p) => p.count),
        backgroundColor: COLORS.slice(0, data.pathwayDistribution.length),
        borderWidth: 2,
        borderColor: sliceBorder,
      },
    ],
  };

  const barData = {
    labels: data.scoreDistribution.map((s) => s.range),
    datasets: [
      {
        label: "Students",
        data: data.scoreDistribution.map((s) => s.count),
        backgroundColor: COLORS[0],
        borderRadius: 4,
      },
    ],
  };

  return (
    <div style={{ padding: "24px 32px", maxWidth: 1200, margin: "0 auto" }}>
      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <h3 style={{ fontSize: 20, fontWeight: 700, margin: 0 }}>
          Interactive Pathway Analytics
        </h3>
        <p style={{ fontSize: 13, color: "var(--text-muted)", margin: "4px 0 0" }}>
          Resource ID: {resourceId}
        </p>
      </div>

      {/* Summary cards */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 16, marginBottom: 28 }}>
        <div style={cardStyle}>
          <div style={{ fontSize: 28, fontWeight: 800, color: "var(--text-strong)" }}>
            {data.summary.totalSubmissions}
          </div>
          <div style={{ fontSize: 12, color: "var(--text-muted)", textTransform: "uppercase", fontWeight: 600 }}>
            Total Submissions
          </div>
        </div>
        <div style={cardStyle}>
          <div style={{ fontSize: 28, fontWeight: 800, color: "#2b6cb0" }}>
            {data.summary.avgScore || "—"}
          </div>
          <div style={{ fontSize: 12, color: "var(--text-muted)", textTransform: "uppercase", fontWeight: 600 }}>
            Avg Score
          </div>
        </div>
        <div style={cardStyle}>
          <div style={{ fontSize: 28, fontWeight: 800, color: "#276749" }}>
            {data.summary.scoredCount}
          </div>
          <div style={{ fontSize: 12, color: "var(--text-muted)", textTransform: "uppercase", fontWeight: 600 }}>
            AI-Scored
          </div>
        </div>
      </div>

      {/* Charts row */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20, marginBottom: 28 }}>
        {/* Pathway Distribution */}
        <div style={cardStyle}>
          <h5 style={{ fontSize: 14, fontWeight: 700, marginBottom: 16 }}>Pathway Distribution</h5>
          <div style={{ maxWidth: 260, margin: "0 auto" }}>
            <Doughnut
              data={doughnutData}
              options={{ plugins: { legend: { position: "bottom", labels: { font: { size: 11 } } } } }}
            />
          </div>
        </div>

        {/* Score Distribution */}
        <div style={cardStyle}>
          <h5 style={{ fontSize: 14, fontWeight: 700, marginBottom: 16 }}>Score Distribution</h5>
          <Bar
            data={barData}
            options={{
              plugins: { legend: { display: false } },
              scales: {
                y: { beginAtZero: true, ticks: { stepSize: 1 } },
                x: { grid: { display: false } },
              },
            }}
          />
        </div>
      </div>

      {/* Submissions table */}
      <div style={cardStyle}>
        <h5 style={{ fontSize: 14, fontWeight: 700, marginBottom: 12 }}>Recent Submissions</h5>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
          <thead>
            <tr style={{ borderBottom: "2px solid var(--border-default)" }}>
              <th style={thStyle}>Student</th>
              <th style={thStyle}>Pathway Chosen</th>
              <th style={thStyle}>Score</th>
              <th style={thStyle}>Module</th>
              <th style={thStyle}>Date</th>
            </tr>
          </thead>
          <tbody>
            {data.recentSubmissions.map((sub) => (
              <React.Fragment key={sub.id}>
                <tr
                  style={{ borderBottom: "1px solid var(--border-subtle)", cursor: sub.aiFeedback ? "pointer" : "default" }}
                  onClick={() => sub.aiFeedback && setExpandedRow(expandedRow === sub.id ? null : sub.id)}
                >
                  <td style={tdStyle}>{sub.userName}</td>
                  <td style={tdStyle}>
                    <span style={{
                      background: "#ebf8ff", color: "#2b6cb0",
                      padding: "2px 8px", borderRadius: 4, fontSize: 11, fontWeight: 600,
                    }}>
                      {sub.pathwayName}
                    </span>
                  </td>
                  <td style={tdStyle}>
                    {sub.score !== null ? (
                      <span style={{ fontWeight: 700, color: sub.score >= 70 ? "#276749" : sub.score >= 40 ? "#975a16" : "#c53030" }}>
                        {sub.score}
                      </span>
                    ) : "—"}
                  </td>
                  <td style={tdStyle}>{sub.module === 2 ? "Tradeoff" : "Binary"}</td>
                  <td style={tdStyle}>
                    {sub.submittedAt ? new Date(sub.submittedAt).toLocaleDateString() : "—"}
                  </td>
                </tr>
                {expandedRow === sub.id && sub.aiFeedback && (
                  <tr>
                    <td colSpan={5} style={{ padding: "12px 16px", background: "var(--surface-page)" }}>
                      <div style={{ fontSize: 12, marginBottom: 8 }}>
                        <strong>AI Feedback:</strong> {sub.aiFeedback}
                      </div>
                      {sub.aiStrengths && sub.aiStrengths.length > 0 && (
                        <div style={{ fontSize: 12, marginBottom: 6 }}>
                          <strong style={{ color: "#276749" }}>Strengths:</strong>{" "}
                          {sub.aiStrengths.join("; ")}
                        </div>
                      )}
                      {sub.aiImprovements && sub.aiImprovements.length > 0 && (
                        <div style={{ fontSize: 12 }}>
                          <strong style={{ color: "#975a16" }}>Improvements:</strong>{" "}
                          {sub.aiImprovements.join("; ")}
                        </div>
                      )}
                      {sub.justification && (
                        <div style={{ fontSize: 12, marginTop: 8, fontStyle: "italic", color: "var(--text-muted)" }}>
                          "{sub.justification}"
                        </div>
                      )}
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const cardStyle: React.CSSProperties = {
  background: "var(--surface-card)",
  borderRadius: 12,
  padding: 20,
  boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
};

const thStyle: React.CSSProperties = {
  textAlign: "left",
  padding: "8px 12px",
  fontSize: 11,
  fontWeight: 700,
  color: "var(--text-muted)",
  textTransform: "uppercase",
};

const tdStyle: React.CSSProperties = {
  padding: "10px 12px",
};

export default PathwayAnalyticsPage;
